/**
 * The day sheet's other three changes — `docs/visit-page.md` §7.9, §15.1,
 * §15.3. No board draws these popups; they are built as Board 11 draws its
 * own (popup 470, the 34px tile, the 14.5/700 title, *Why* as Board 17 F
 * asks it) and say only words the boards or the contract write (lead, round
 * 2 item 10):
 *
 * - **Skip this day** asks why and marks the day `skipped`; its hours and
 *   materials *stay on the visit for another day*, or *are removed* — the
 *   popup asks (§15.3).
 * - **Change crew** — a crew picker for that day, free crews first; moves the
 *   day's assignments and tells both crews (§7.9).
 * - ⋯ **Remove this day** — only for a day not started, with a reason
 *   (§15.1). Nothing is deleted: the day is cancelled with who, when and why.
 */
import { useId, useState } from 'react'
import type { DayCrewOptions, DaySheet as DaySheetRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { FaceRow } from './face.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER_OVER } from './sheet.js'
import { FieldLabel, RadioDot } from './fields.js'
import { DAY_SHEET } from './copy.js'
import { dayText } from './format.js'

interface DayPopupProps {
  visitId: string
  /** The day sheet's read — the day's index and date. */
  sheet: DaySheetRead
  phone?: boolean
  layer?: number
  onClose: () => void
}

/** *Day 2 · Thu Oct 22* — the popup's sub. */
const subOf = (sheet: DaySheetRead): string => DAY_SHEET.daySub(String(sheet.day.index), dayText(sheet.day.date, 'date'))

/** **Why** — required, as Board 17 F draws it: the label with the green dot, a 56px box. */
function WhyBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const id = useId()
  return (
    <div className="min-w-0">
      <FieldLabel required htmlFor={id}>{DAY_SHEET.why}</FieldLabel>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoFocus
        className="block h-14 w-full resize-none tabular-nums rounded-button border border-line bg-surface px-3 py-2.5 text-[13px]
                   leading-normal text-text focus:border-accent-line focus:outline-none"
      />
    </div>
  )
}

function useSend(visitId: string) {
  const write = useVisitWrite(visitId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const send = async (method: 'POST' | 'PUT', path: string, body: unknown): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      await write(method, path, body, { tab: 'overview' })
      return true
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { send, busy, error }
}

/** **Skip this day** — Why, and what happens to its hours and materials. */
export function SkipDayPopup({ visitId, sheet, phone: forced, layer = VISIT_LAYER_OVER, onClose, onSkipped }: DayPopupProps & {
  onSkipped: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const [why, setWhy] = useState('')
  const [keep, setKeep] = useState<'keep' | 'remove'>('keep')
  const { send, busy, error } = useSend(visitId)
  const size = phone ? 'phone' : 'popup'
  const ready = why.trim().length > 0 && !busy

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={DAY_SHEET.skip}
      sub={subOf(sheet)}
      mark={<VisitTile glyph="calendar" tone="grey" size={34} />}
      onClose={onClose}
      layer={layer}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{DAY_SHEET.cancel}</VisitButton>
          <VisitButton
            size={size}
            tone="primary"
            disabled={!ready}
            onClick={() => void send('POST', `/days/${sheet.day.index}/skip`, { reason: why.trim(), hoursAndMaterials: keep })
              .then((ok) => { if (ok) onSkipped() })}
          >
            {DAY_SHEET.skip}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <WhyBox value={why} onChange={setWhy} />
        <div role="radiogroup" aria-label={DAY_SHEET.skip} className="flex flex-col gap-[7px]">
          {([['keep', DAY_SHEET.keep], ['remove', DAY_SHEET.drop]] as const).map(([key, words]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={keep === key}
              onClick={() => setKeep(key)}
              className={cn('flex items-center gap-2.5 rounded-[10px] border-[1.5px] px-3 py-2.5 text-left',
                keep === key ? 'border-accent bg-green-band-bg' : 'border-line bg-surface hover:bg-hover')}
            >
              <RadioDot on={keep === key} size={17} />
              <b className="text-[12.5px]">{words}</b>
            </button>
          ))}
        </div>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}

/** ⋯ **Remove this day** — a day not started, with a reason. */
export function RemoveDayPopup({ visitId, sheet, phone: forced, layer = VISIT_LAYER_OVER, onClose, onRemoved }: DayPopupProps & {
  onRemoved: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const [why, setWhy] = useState('')
  const { send, busy, error } = useSend(visitId)
  const size = phone ? 'phone' : 'popup'
  const ready = why.trim().length > 0 && !busy

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={DAY_SHEET.remove}
      sub={subOf(sheet)}
      mark={<VisitTile glyph="close" tone="danger" size={34} />}
      onClose={onClose}
      layer={layer}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{DAY_SHEET.cancel}</VisitButton>
          <VisitButton
            size={size}
            tone="danger"
            disabled={!ready}
            onClick={() => void send('POST', `/days/${sheet.day.index}/remove`, { reason: why.trim() })
              .then((ok) => { if (ok) onRemoved() })}
          >
            {DAY_SHEET.remove}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <WhyBox value={why} onChange={setWhy} />
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}

/** **Change crew** — the crews for that day, free ones first (`GET …/days/:n/crew-options`). */
export function ChangeCrewPopup({ visitId, sheet, phone: forced, layer = VISIT_LAYER_OVER, onClose, onChanged }: DayPopupProps & {
  onChanged: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const options = useVisitTab<DayCrewOptions>(visitId, `/days/${sheet.day.index}/crew-options`)
  const [picked, setPicked] = useState<string | null>(null)
  const { send, busy, error } = useSend(visitId)
  const size = phone ? 'phone' : 'popup'
  const current = options.data?.current?.id ?? null
  const chosen = picked ?? current
  const ready = Boolean(chosen && chosen !== current) && !busy

  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={DAY_SHEET.changeCrew}
      sub={subOf(sheet)}
      mark={<VisitTile glyph="people" tone="accent" size={34} />}
      onClose={onClose}
      layer={layer}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{DAY_SHEET.cancel}</VisitButton>
          <VisitButton
            size={size}
            tone="primary"
            glyph={phone ? undefined : 'people'}
            disabled={!ready}
            onClick={() => void send('PUT', `/days/${sheet.day.index}/crew`, { crewId: chosen })
              .then((ok) => { if (ok) onChanged() })}
          >
            {DAY_SHEET.changeCrew}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div role="radiogroup" aria-label={DAY_SHEET.crew} className="flex flex-col gap-[7px]">
          {options.data?.crews.map((one) => {
            const on = one.crew.id === chosen
            return (
              <button
                key={one.crew.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPicked(one.crew.id)}
                className={cn('flex items-center gap-2.5 rounded-[10px] border-[1.5px] px-3 text-left',
                  phone ? 'min-h-12 py-2' : 'py-2.5',
                  on ? 'border-accent bg-green-band-bg' : 'border-line bg-surface hover:bg-hover',
                  !one.free && !on && 'opacity-45')}
              >
                <RadioDot on={on} size={17} />
                <FaceRow people={one.people} size={20} />
                <b className="min-w-0 flex-1 truncate text-[12.5px]">{one.crew.name}</b>
              </button>
            )
          })}
        </div>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
